// 语音输入：输入框旁边的麦克风。点一下开始说，字边说边进输入框（放在光标处）；再点一下停。
// 说话时按钮变成一条小胶囊：跟着音量跳的竖条 + 秒数。15 秒没听到声音、说满 5 分钟都会自己停。
// 识别用阿里云百炼的实时识别（经 lingzhi-studio 转，Key 不在网页里）；音频和文字不存。模型在设置 → 语音输入里选（只记在本机）。

import { useEffect, useRef, useState, useSyncExternalStore, type RefObject } from 'react'
import { App as AntApp } from 'antd'
import { LoaderCircle, Mic } from 'lucide-react'
import { IconButton } from '../../ui'
import { captureProblem } from './capture'
import { Dictation, defaultDeps, type DictationDeps, type DictationState } from './dictation'
import './voice.css'

// 2026-10-12 在线上用同样两段录音比过（中英夹杂一段、长句一段）：千问 3.1 / 3.0 都写对「灵智原生」，3.0 的英文大小写更规整；
// Fun-ASR 收尾最快，但不吃直接给的热词，「灵智原生」写成「私库」；Paraformer 把 DeepSeek、Qwen 听错，没放进来。
export const VOICE_MODELS = [
  { id: 'qwen-audio-3.1-asr-flash-streaming', label: '千问 3.1', note: '推荐。阿里云给实时识别推荐的最新版，认得「灵智原生」，中英夹杂、方言、嘈杂的地方更稳' },
  { id: 'qwen-audio-3.0-asr-flash-streaming', label: '千问 3.0', note: '上一版，也认得「灵智原生」，英文大小写更规整（DeepSeek、Qwen）' },
  { id: 'fun-asr-realtime', label: 'Fun-ASR', note: '说完出字最快，但「灵智原生」会写成「私库」' },
] as const

const KEY = 'lingzhi-web.voice-model'
const listeners = new Set<() => void>()
const readModel = () => {
  try {
    const v = localStorage.getItem(KEY)
    return VOICE_MODELS.some((m) => m.id === v) ? (v as string) : VOICE_MODELS[0].id
  } catch {
    return VOICE_MODELS[0].id
  }
}
export function setVoiceModel(id: string) {
  try {
    localStorage.setItem(KEY, id)
  } catch {
    // 记不住也没关系
  }
  listeners.forEach((l) => l())
}
export const useVoiceModel = () =>
  useSyncExternalStore(
    (l) => (listeners.add(l), () => listeners.delete(l)),
    readModel,
    () => VOICE_MODELS[0].id,
  )

type Field = HTMLTextAreaElement | HTMLInputElement

export interface DictationHandle {
  state: DictationState
  level: number
  seconds: number
  /** 在说或在收尾：输入框先别让人改（改了会和识别出来的字打架）。 */
  active: boolean
  toggle: () => void
  /** 说完了：等最后几个字，返回输入框里的全文（发送前用）。 */
  stop: () => Promise<string>
}

/** 把一个输入框接上语音输入。value / onChange 是这个输入框受控的那一对；onDone：说完、字都进来了（失焦才存的输入框在这里存）。 */
export function useDictation({ value, onChange, onDone, inputRef, deps }: { value: string; onChange: (v: string) => void; onDone?: (v: string) => void; inputRef?: RefObject<Field | null>; deps?: () => DictationDeps }): DictationHandle {
  const { message } = AntApp.useApp()
  const model = useVoiceModel()
  const [state, setState] = useState<DictationState>('idle')
  const [level, setLevel] = useState(0)
  const [seconds, setSeconds] = useState(0)
  const live = useRef<Dictation | null>(null)
  const latest = useRef({ value, onChange, onDone })
  latest.current = { value, onChange, onDone }

  useEffect(() => () => live.current?.cancel(), [])

  const start = () => {
    if (live.current) return
    const problem = deps ? null : captureProblem()
    if (problem) return void message.warning(problem)
    const el = inputRef?.current
    const v = latest.current.value
    const s = el?.selectionStart ?? v.length
    const e = el?.selectionEnd ?? v.length
    setLevel(0)
    setSeconds(0)
    const d = new Dictation(
      { value: v, start: Math.min(s, v.length), end: Math.min(e, v.length) },
      {
        onState: (st) => {
          setState(st)
          if (st === 'idle') {
            live.current = null
            setLevel(0)
            latest.current.onDone?.(d.value)
          }
        },
        onText: (next, caret) => {
          latest.current.onChange(next)
          requestAnimationFrame(() => {
            const f = inputRef?.current
            if (f && document.activeElement === f) f.setSelectionRange(caret, caret)
          })
        },
        onLevel: (l, sec) => {
          setLevel(l)
          setSeconds(sec)
        },
        onError: (m) => void message.error(m),
        onNote: (m) => void message.info(m),
      },
      deps ? deps() : defaultDeps(model),
    )
    live.current = d
    void d.start()
  }

  return {
    state,
    level,
    seconds,
    active: state !== 'idle',
    toggle: () => (live.current ? void live.current.stop() : start()),
    stop: () => (live.current ? live.current.stop() : Promise.resolve(latest.current.value)),
  }
}

const clock = (s: number) => `${Math.floor(s / 60)}:${String(Math.floor(s % 60)).padStart(2, '0')}`

/** 麦克风按钮。说话时变成「竖条 + 秒数」的胶囊，点它就停。 */
export function MicButton({ d, className }: { d: DictationHandle; className?: string }) {
  if (d.state === 'idle')
    return (
      <IconButton label="语音输入" className={['mic-btn', className].filter(Boolean).join(' ')} onClick={d.toggle}>
        <Mic />
      </IconButton>
    )
  const busy = d.state !== 'listening'
  return (
    <button type="button" className={['mic-live', className].filter(Boolean).join(' ')} data-state={d.state} onClick={d.toggle} disabled={d.state === 'finishing'} aria-label={d.state === 'finishing' ? '正在收尾' : '停止语音输入'} title={d.state === 'finishing' ? '正在收尾' : '点一下停'}>
      {busy ? (
        <LoaderCircle className="mic-spin" aria-hidden="true" />
      ) : (
        <span className="mic-bars" aria-hidden="true">
          {[0.55, 0.85, 1, 0.75, 0.5].map((k, i) => (
            <i key={i} style={{ transform: `scaleY(${Math.max(0.18, Math.min(1, d.level * k * 1.6))})` }} />
          ))}
        </span>
      )}
      <span className="mic-time">{d.state === 'starting' ? '准备中' : d.state === 'finishing' ? '收尾' : clock(d.seconds)}</span>
    </button>
  )
}
